import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Field, SearchSelect } from '@evogard/ui';
import type {
  RepairQuotes as RepairQuotesData,
  SaveRepairQuoteBody,
} from '@evogard/contract';
import {
  REPAIR_PART_ORIGINS,
  REPAIR_PART_ORIGIN_LABELS,
  REPAIR_PART_SUPPLIERS,
  REPAIR_PART_SUPPLIER_LABELS,
  REPAIR_QUOTE_COVERAGES,
  REPAIR_QUOTE_COVERAGE_LABELS,
  REPAIR_QUOTE_SOURCE_LABELS,
  REPAIR_QUOTE_SOURCE_NAMES,
  compareRepairQuotes,
  format,
  formatInstant,
  fromColumn,
  moneyInputText,
  parseMoneyInput,
  quoteItemNet,
  quoteItemTotal,
  repairQuoteTotals,
  toColumn,
  ZERO,
  type Money,
  type RepairPartOrigin,
  type RepairPartSupplier,
  type RepairQuoteChange,
  type RepairQuoteCoverage,
  type RepairQuoteItem,
  type RepairQuoteItemKind,
  type RepairQuoteRates,
  type RepairQuoteSource,
} from '@evogard/domain';
import { Section, Wait } from './RepairBlock.js';
import { useRepairQuotes, useSaveRepairQuote } from './useDesk.js';

/**
 * O orçamento item a item do Reparo (03/11/2026, proposta da Orçamentação do
 * Reparo), no alto do formulário de três etapas:
 *
 * - **Orçamentação** (`editar`): monta o orçamento próprio e digita o da
 *   CILIA na mesma forma — áreas de impacto, e em cada linha as horas por
 *   operação, a peça, o tipo, quem fornece, a cobertura e o preço. A conta é
 *   a mesma função da API (`repairQuoteTotals`), refeita a cada tecla.
 * - **Aprovação Supervisor** (`aprovar`): os dois lado a lado e as linhas que
 *   mudaram, para o supervisor escolher em "Orçamento aprovado" (A3).
 * - **Compra de Peças** (`pecas`): as peças que a associação compra, do
 *   orçamento aprovado (A7) — lidas dele, e não copiadas.
 */
export type RepairQuotesMode = 'editar' | 'aprovar' | 'pecas';

/* ------------------------------------------------------------------------- *
 * O rascunho — o que a pessoa digita, em texto, até salvar
 * ------------------------------------------------------------------------- */

interface DraftItem {
  key: number;
  impactArea: string;
  kind: RepairQuoteItemKind;
  replaceHours: string;
  removeInstallHours: string;
  repairHours: string;
  paintHours: string;
  quantity: string;
  partCode: string;
  partOrigin: RepairPartOrigin | '';
  description: string;
  supplier: RepairPartSupplier;
  unitPrice: string;
  discountAmount: string;
  coverage: RepairQuoteCoverage;
}
interface Draft {
  rates: { labor: string; repair: string; paint: string };
  items: DraftItem[];
}

let nextKey = 1;
type ServerQuote = RepairQuotesData['quotes']['proprio'];

const hoursText = (v: string | null) => (v === null ? '' : v.replace('.', ','));
const moneyText = (v: string | null) => {
  const m = fromColumn(v);
  return m === null ? '' : moneyInputText(m);
};
/** "1,5" → 1.5; vazio é nulo; o que não é número também (a linha acende). */
function hoursOf(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}
const isBadHours = (text: string) => text.trim() !== '' && hoursOf(text) === null;
const isBadMoney = (text: string) => text.trim() !== '' && parseMoneyInput(text) === null;

function draftOf(q: ServerQuote): Draft {
  return {
    rates: {
      labor: moneyText(q.rates.labor),
      repair: moneyText(q.rates.repair),
      paint: moneyText(q.rates.paint),
    },
    items: q.items.map((i) => ({
      key: nextKey++,
      impactArea: i.impactArea,
      kind: i.kind,
      replaceHours: hoursText(i.replaceHours),
      removeInstallHours: hoursText(i.removeInstallHours),
      repairHours: hoursText(i.repairHours),
      paintHours: hoursText(i.paintHours),
      quantity: hoursText(i.quantity),
      partCode: i.partCode ?? '',
      partOrigin: i.partOrigin ?? '',
      description: i.description,
      supplier: i.supplier,
      unitPrice: moneyText(i.unitPrice),
      discountAmount: moneyText(i.discountAmount),
      coverage: i.coverage,
    })),
  };
}

function ratesOf(d: Draft['rates']): RepairQuoteRates {
  return {
    labor: parseMoneyInput(d.labor),
    repair: parseMoneyInput(d.repair),
    paint: parseMoneyInput(d.paint),
  };
}

function domainItem(i: DraftItem): RepairQuoteItem {
  return {
    impactArea: i.impactArea,
    kind: i.kind,
    replaceHours: hoursOf(i.replaceHours),
    removeInstallHours: hoursOf(i.removeInstallHours),
    repairHours: hoursOf(i.repairHours),
    paintHours: hoursOf(i.paintHours),
    quantity: hoursOf(i.quantity) ?? 1,
    partCode: i.partCode.trim() || null,
    partOrigin: i.kind === 'servico' ? null : i.partOrigin || null,
    description: i.description,
    supplier: i.supplier,
    unitPrice: parseMoneyInput(i.unitPrice),
    discountAmount: parseMoneyInput(i.discountAmount),
    coverage: i.coverage,
  };
}

/** O orçamento salvo, na forma da conta — o que a comparação e as peças leem. */
function serverDomain(q: ServerQuote): {
  items: RepairQuoteItem[];
  rates: RepairQuoteRates;
} {
  return {
    items: draftOf(q).items.map(domainItem),
    rates: {
      labor: fromColumn(q.rates.labor),
      repair: fromColumn(q.rates.repair),
      paint: fromColumn(q.rates.paint),
    },
  };
}

function bodyOf(d: Draft): SaveRepairQuoteBody {
  const money = (t: string) => {
    const m = parseMoneyInput(t);
    return m === null ? null : toColumn(m);
  };
  const hours = (t: string) => {
    const h = hoursOf(t);
    return h === null || h === 0 ? null : String(h);
  };
  return {
    rates: {
      labor: money(d.rates.labor),
      repair: money(d.rates.repair),
      paint: money(d.rates.paint),
    },
    items: d.items.map((i) => ({
      impactArea: i.impactArea.trim(),
      kind: i.kind,
      replaceHours: hours(i.replaceHours),
      removeInstallHours: hours(i.removeInstallHours),
      repairHours: hours(i.repairHours),
      paintHours: hours(i.paintHours),
      quantity: String(hoursOf(i.quantity) || 1),
      partCode: i.partCode.trim() || null,
      partOrigin: i.kind === 'servico' ? null : i.partOrigin || null,
      description: i.description.trim(),
      supplier: i.supplier,
      unitPrice: money(i.unitPrice),
      discountAmount: money(i.discountAmount),
      coverage: i.coverage,
    })),
  };
}

const blankItem = (impactArea: string, kind: RepairQuoteItemKind): DraftItem => ({
  key: nextKey++,
  impactArea,
  kind,
  replaceHours: '',
  removeInstallHours: '',
  repairHours: '',
  paintHours: '',
  quantity: '1',
  partCode: '',
  partOrigin: kind === 'peca' ? 'genuina' : '',
  description: '',
  supplier: 'oficina',
  unitPrice: '',
  discountAmount: '',
  coverage: 'impacto',
});

/** As áreas de impacto, na ordem em que aparecem. */
function areasOf<T extends { impactArea: string }>(items: readonly T[]): string[] {
  return [...new Set(items.map((i) => i.impactArea))];
}

/* ------------------------------------------------------------------------- *
 * O bloco
 * ------------------------------------------------------------------------- */

export function RepairQuotes({
  claimId,
  mode,
}: {
  claimId: number;
  mode: RepairQuotesMode;
}) {
  const quotes = useRepairQuotes(claimId);
  if (quotes.isError) return <Alert tone="erro">{quotes.error.message}</Alert>;
  if (!quotes.data)
    return <p className="text-sm text-texto-suave">Carregando o orçamento…</p>;
  if (mode === 'pecas') return <AssociationParts data={quotes.data} />;
  if (mode === 'aprovar') return <QuoteReview data={quotes.data} />;
  return (
    <QuoteEditor key={editorKey(quotes.data)} claimId={claimId} data={quotes.data} />
  );
}

/** Recomeça o rascunho quando o que está salvo muda. */
const editorKey = (d: RepairQuotesData) =>
  `${d.quotes.proprio.savedAt ?? '-'}|${d.quotes.cilia.savedAt ?? '-'}|${d.editable}`;

function participationOf(data: RepairQuotesData): Money {
  return fromColumn(data.participation) ?? ZERO;
}

/* ------------------------------------------------------------------------- *
 * Orçamentação — o editor
 * ------------------------------------------------------------------------- */

function QuoteEditor({ claimId, data }: { claimId: number; data: RepairQuotesData }) {
  const [source, setSource] = useState<RepairQuoteSource>('proprio');
  const [drafts, setDrafts] = useState<Record<RepairQuoteSource, Draft>>(() => ({
    proprio: draftOf(data.quotes.proprio),
    cilia: draftOf(data.quotes.cilia),
  }));
  const [dirty, setDirty] = useState<Record<RepairQuoteSource, boolean>>({
    proprio: false,
    cilia: false,
  });
  const save = useSaveRepairQuote(claimId);
  const draft = drafts[source];
  const editable = data.editable;
  const participation = participationOf(data);

  const set = (next: Draft) => {
    setDrafts((d) => ({ ...d, [source]: next }));
    setDirty((d) => ({ ...d, [source]: true }));
  };
  const setItem = (key: number, patch: Partial<DraftItem>) =>
    set({
      ...draft,
      items: draft.items.map((i) => (i.key === key ? { ...i, ...patch } : i)),
    });
  const addItem = (area: string, kind: RepairQuoteItemKind) => {
    /* A linha nova entra no fim da área dela. */
    const last = draft.items.map((i) => i.impactArea).lastIndexOf(area);
    const items = [...draft.items];
    items.splice(last + 1, 0, blankItem(area, kind));
    set({ ...draft, items });
  };
  const renameArea = (from: string, to: string) =>
    set({
      ...draft,
      items: draft.items.map((i) =>
        i.impactArea === from ? { ...i, impactArea: to } : i,
      ),
    });

  const items = useMemo(() => draft.items.map(domainItem), [draft.items]);
  const rates = ratesOf(draft.rates);
  const totals = repairQuoteTotals(items, rates, participation);
  const areas = areasOf(draft.items);
  const problems =
    draft.items.some(
      (i) =>
        !i.description.trim() ||
        !i.impactArea.trim() ||
        [
          i.replaceHours,
          i.removeInstallHours,
          i.repairHours,
          i.paintHours,
          i.quantity,
        ].some(isBadHours) ||
        [i.unitPrice, i.discountAmount].some(isBadMoney),
    ) || [draft.rates.labor, draft.rates.repair, draft.rates.paint].some(isBadMoney);
  const other: RepairQuoteSource = source === 'proprio' ? 'cilia' : 'proprio';
  const saved = data.quotes[source];

  return (
    <Section
      title="Orçamento item a item"
      aside={
        saved.savedAt
          ? `salvo em ${formatInstant(saved.savedAt)}${saved.savedByName ? ` por ${saved.savedByName}` : ''}`
          : 'ainda não salvo'
      }
    >
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Qual orçamento"
      >
        {(['proprio', 'cilia'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={s === source ? 'primario' : 'secundario'}
            aria-pressed={s === source}
            onClick={() => setSource(s)}
          >
            {REPAIR_QUOTE_SOURCE_LABELS[s]}
            {dirty[s] ? ' •' : ''}
          </Button>
        ))}
        {!editable && <Badge tone="neutro">só leitura</Badge>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(
          [
            ['labor', 'Mão de obra · R$/h', 'Troca e R&I.'],
            ['repair', 'Reparação · R$/h', undefined],
            ['paint', 'Pintura · R$/h', undefined],
          ] as const
        ).map(([key, label, help]) => (
          <Field
            key={key}
            label={label}
            mask="money"
            prefix="R$"
            value={draft.rates[key]}
            disabled={!editable}
            error={isBadMoney(draft.rates[key]) ? 'Valor inválido.' : null}
            help={help}
            onChange={(e) =>
              set({ ...draft, rates: { ...draft.rates, [key]: e.target.value } })
            }
          />
        ))}
      </div>

      {draft.items.length === 0 && (
        <Wait>
          {source === 'proprio'
            ? 'O orçamento próprio ainda não tem linhas.'
            : 'O orçamento da CILIA ainda não foi digitado: as linhas dele entram aqui, como vieram no PDF.'}
        </Wait>
      )}

      {areas.map((area) => (
        <AreaTable
          key={area}
          area={area}
          items={draft.items.filter((i) => i.impactArea === area)}
          rates={rates}
          editable={editable}
          onRename={(to) => renameArea(area, to)}
          onChange={setItem}
          onRemove={(key) =>
            set({ ...draft, items: draft.items.filter((i) => i.key !== key) })
          }
          onAdd={(kind) => addItem(area, kind)}
        />
      ))}

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="contorno"
            onClick={() =>
              set({
                ...draft,
                items: [
                  ...draft.items,
                  blankItem(`Item ${areas.length + 1}`, 'peca'),
                ],
              })
            }
          >
            Novo item ou serviço
          </Button>
          {source === 'proprio' &&
            draft.items.length === 0 &&
            drafts.cilia.items.length > 0 && (
              <Button
                size="sm"
                variant="discreto"
                onClick={() =>
                  set({
                    rates: { ...drafts.cilia.rates },
                    items: drafts.cilia.items.map((i) => ({ ...i, key: nextKey++ })),
                  })
                }
              >
                Começar pela CILIA
              </Button>
            )}
        </div>
      )}

      {draft.items.length > 0 && (
        <Ledger totals={totals} fipe={fromColumn(data.fipeValue)} />
      )}

      {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!dirty[source] || problems}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { source, body: bodyOf(draft) },
                { onSuccess: () => setDirty((d) => ({ ...d, [source]: false })) },
              )
            }
          >
            Salvar o {REPAIR_QUOTE_SOURCE_NAMES[source]}
          </Button>
          {dirty[source] && (
            <Button
              size="sm"
              variant="discreto"
              onClick={() => {
                setDrafts((d) => ({ ...d, [source]: draftOf(data.quotes[source]) }));
                setDirty((d) => ({ ...d, [source]: false }));
              }}
            >
              Descartar
            </Button>
          )}
          {problems && (
            <span className="text-xs text-erro">
              Há linha sem título ou com número inválido.
            </span>
          )}
        </div>
      )}

      {data.quotes[other].items.length > 0 && drafts[other].items.length > 0 && (
        <Comparison
          first={{
            label: REPAIR_QUOTE_SOURCE_LABELS.cilia,
            ...(source === 'cilia' ? { items, rates } : serverDomain(data.quotes.cilia)),
          }}
          second={{
            label: REPAIR_QUOTE_SOURCE_LABELS.proprio,
            ...(source === 'proprio'
              ? { items, rates }
              : serverDomain(data.quotes.proprio)),
          }}
          participation={participation}
        />
      )}
    </Section>
  );
}

const ORIGIN_OPTIONS = REPAIR_PART_ORIGINS.map((o) => ({
  value: o,
  label: REPAIR_PART_ORIGIN_LABELS[o],
}));
const SUPPLIER_OPTIONS = REPAIR_PART_SUPPLIERS.map((o) => ({
  value: o,
  label: REPAIR_PART_SUPPLIER_LABELS[o],
}));
const COVERAGE_OPTIONS = REPAIR_QUOTE_COVERAGES.map((o) => ({
  value: o,
  label: REPAIR_QUOTE_COVERAGE_LABELS[o],
}));

const OPS = [
  ['replaceHours', 'T', 'Troca'],
  ['removeInstallHours', 'R&I', 'Remoção e instalação'],
  ['repairHours', 'R', 'Reparação'],
  ['paintHours', 'P', 'Pintura'],
] as const;

/**
 * Uma área de impacto. Cada linha é um cartão de duas faixas, e não uma
 * coluna por campo: são treze campos, e a mesa tem a largura de um formulário
 * — numa tabela, a quantidade e o código saíam cortados e a linha rolava de
 * lado (percorrido em 03/11/2026). Em cima, o que a peça é; embaixo, a conta.
 */
function AreaTable({
  area,
  items,
  rates,
  editable,
  onRename,
  onChange,
  onRemove,
  onAdd,
}: {
  area: string;
  items: DraftItem[];
  rates: RepairQuoteRates;
  editable: boolean;
  onRename: (to: string) => void;
  onChange: (key: number, patch: Partial<DraftItem>) => void;
  onRemove: (key: number) => void;
  onAdd: (kind: RepairQuoteItemKind) => void;
}) {
  const [name, setName] = useState(area);
  const areaTotal = items
    .map(domainItem)
    .filter((i) => i.coverage !== 'sem_amparo')
    .reduce<Money>((acc, i) => (acc + quoteItemTotal(i, rates)) as Money, ZERO);
  return (
    <div className="overflow-hidden rounded-borda border border-borda">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-superficie-suave px-3 py-2">
        {editable ? (
          <div className="w-64 max-w-full">
            <Field
              label="Nome do item ou serviço"
              labelHidden
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name !== area && onRename(name.trim())}
            />
          </div>
        ) : (
          <span className="text-sm font-semibold">{area}</span>
        )}
        <span className="text-sm tabular-nums text-texto-suave">
          {items.length} {items.length === 1 ? 'linha' : 'linhas'} · {format(areaTotal)}
        </span>
      </div>
      <ol className="flex flex-col">
        {items.map((i, n) => {
          const d = domainItem(i);
          const row = `linha ${n + 1}${i.description ? ` (${i.description})` : ''}`;
          const uncovered = i.coverage === 'sem_amparo';
          return (
            <li
              key={i.key}
              className={
                uncovered
                  ? 'flex flex-col gap-2 border-t border-borda-suave px-3 py-2.5 opacity-70'
                  : 'flex flex-col gap-2 border-t border-borda-suave px-3 py-2.5'
              }
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
                <div className="col-span-2 sm:col-span-4">
                  <Field
                    label={`Título, ${row}`}
                    labelHidden
                    placeholder={i.kind === 'servico' ? 'Serviço' : 'Peça'}
                    value={i.description}
                    disabled={!editable}
                    onChange={(e) => onChange(i.key, { description: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label={`Código da peça, ${row}`}
                    labelHidden
                    placeholder="Código"
                    value={i.partCode}
                    disabled={!editable || i.kind === 'servico'}
                    onChange={(e) => onChange(i.key, { partCode: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  {i.kind === 'servico' ? (
                    <span className="flex h-9 items-center text-xs text-texto-suave">
                      serviço
                    </span>
                  ) : (
                    <SearchSelect
                      label={`Tipo da peça, ${row}`}
                      hiddenLabel
                      options={ORIGIN_OPTIONS}
                      value={i.partOrigin}
                      disabled={!editable}
                      onChange={(v) =>
                        onChange(i.key, { partOrigin: v as RepairPartOrigin | '' })
                      }
                    />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <SearchSelect
                    label={`Quem fornece, ${row}`}
                    hiddenLabel
                    options={SUPPLIER_OPTIONS}
                    value={i.supplier}
                    disabled={!editable || i.kind === 'servico'}
                    onChange={(v) =>
                      onChange(i.key, { supplier: v as RepairPartSupplier })
                    }
                  />
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <SearchSelect
                    label={`Cobertura, ${row}`}
                    hiddenLabel
                    options={COVERAGE_OPTIONS}
                    value={i.coverage}
                    disabled={!editable}
                    onChange={(v) =>
                      onChange(i.key, { coverage: v as RepairQuoteCoverage })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-2 sm:grid-cols-12">
                {OPS.map(([key, short, label]) => (
                  <div key={key} title={`${label}, em horas`}>
                    <Field
                      label={`${label} em horas, ${row}`}
                      labelHidden
                      placeholder={short}
                      inputMode="decimal"
                      value={i[key]}
                      disabled={!editable}
                      error={isBadHours(i[key]) ? 'Inválido' : null}
                      onChange={(e) => onChange(i.key, { [key]: e.target.value })}
                    />
                  </div>
                ))}
                <div title="Quantidade">
                  <Field
                    label={`Quantidade, ${row}`}
                    labelHidden
                    placeholder="Qtd."
                    inputMode="decimal"
                    value={i.quantity}
                    disabled={!editable}
                    error={isBadHours(i.quantity) ? 'Inválido' : null}
                    onChange={(e) => onChange(i.key, { quantity: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Field
                    label={`Preço, ${row}`}
                    labelHidden
                    prefix="R$"
                    mask="money"
                    placeholder="Preço"
                    value={i.unitPrice}
                    disabled={!editable}
                    error={isBadMoney(i.unitPrice) ? 'Inválido' : null}
                    onChange={(e) => onChange(i.key, { unitPrice: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Field
                    label={`Desconto, ${row}`}
                    labelHidden
                    prefix="−"
                    mask="money"
                    placeholder="Desconto"
                    value={i.discountAmount}
                    disabled={!editable}
                    error={isBadMoney(i.discountAmount) ? 'Inválido' : null}
                    onChange={(e) => onChange(i.key, { discountAmount: e.target.value })}
                  />
                </div>
                <span
                  className="col-span-2 text-right text-sm font-semibold tabular-nums whitespace-nowrap"
                  title="Peça e mão de obra da linha"
                >
                  {format(quoteItemTotal(d, rates))}
                </span>
                {editable ? (
                  <Button
                    size="sm"
                    variant="discreto"
                    aria-label={`Tirar a ${row}`}
                    onClick={() => onRemove(i.key)}
                  >
                    Tirar
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {editable && (
        <div className="flex gap-2 border-t border-borda-suave px-3 py-2">
          <Button size="sm" variant="contorno" onClick={() => onAdd('peca')}>
            Nova peça
          </Button>
          <Button size="sm" variant="contorno" onClick={() => onAdd('servico')}>
            Novo serviço
          </Button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * O resumo — a mesma conta da API
 * ------------------------------------------------------------------------- */

function Ledger({
  totals,
  fipe,
}: {
  totals: ReturnType<typeof repairQuoteTotals>;
  fipe: Money | null;
}) {
  const hours = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  const rows: [string, Money, string?][] = [
    ['Peças pela oficina', totals.partsByWorkshop],
    ['Peças pela associação', totals.partsByAssociation],
    [
      `Troca e R&I · ${hours(totals.hours.replaceAndRemove)} h`,
      totals.labor.replaceAndRemove,
      'sub',
    ],
    [`Reparação · ${hours(totals.hours.repair)} h`, totals.labor.repair, 'sub'],
    [`Pintura · ${hours(totals.hours.paint)} h`, totals.labor.paint, 'sub'],
    ['Serviços', totals.services, 'sub'],
    ['Mão de obra', totals.laborTotal],
    ['Total geral', totals.grossTotal, 'total'],
    ['Participação (PI)', totals.participation, 'sub'],
    ['Líquido geral', totals.netTotal, 'total'],
  ];
  const share = fipe && fipe > 0 ? Math.round((totals.grossTotal / fipe) * 100) : null;
  return (
    <div className="grid gap-3 rounded-borda border border-borda p-3 sm:grid-cols-2">
      <dl className="flex flex-col text-sm">
        {rows.map(([label, value, kind]) => (
          <div
            key={label}
            className={
              kind === 'total'
                ? 'flex justify-between border-t border-texto py-1 font-semibold'
                : kind === 'sub'
                  ? 'flex justify-between py-0.5 pl-3 text-texto-suave'
                  : 'flex justify-between py-0.5'
            }
          >
            <dt>{label}</dt>
            <dd className="tabular-nums">
              {label === 'Participação (PI)' && value > 0 ? '− ' : ''}
              {format(value)}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-2 text-sm">
        {totals.memberShare > 0 && (
          <p>
            <span className="font-semibold">{format(totals.memberShare)}</span> sem
            amparo: fica fora do total, e é valor do associado.
          </p>
        )}
        {totals.underReview > 0 && (
          <p>
            <span className="font-semibold">{format(totals.underReview)}</span> sob
            análise: já estão no total.
          </p>
        )}
        {share !== null ? (
          <p>
            O total geral é <span className="font-semibold">{share}% da FIPE</span> (
            {format(fipe!)}). O limite de perda total é do regulamento: a tela não sugere
            destino.
          </p>
        ) : (
          <p className="text-texto-suave">
            Sem valor FIPE no sinistro, não há percentual.
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * CILIA × próprio
 * ------------------------------------------------------------------------- */

const CHANGE_LABELS: Record<RepairQuoteChange, string> = {
  only_first: 'só na CILIA',
  only_second: 'só no próprio',
  price: 'preço',
  origin: 'tipo da peça',
  supplier: 'quem fornece',
  coverage: 'cobertura',
  hours: 'horas',
};

type Side = { label: string; items: RepairQuoteItem[]; rates: RepairQuoteRates };

function Comparison({
  first,
  second,
  participation,
}: {
  first: Side;
  second: Side;
  participation: Money;
}) {
  const a = repairQuoteTotals(first.items, first.rates, participation);
  const b = repairQuoteTotals(second.items, second.rates, participation);
  const diff = compareRepairQuotes(first, second);
  const delta = (x: Money, y: Money) => {
    const d = (y - x) as Money;
    return d === 0 ? 'igual' : `${d > 0 ? '+' : '−'} ${format(Math.abs(d) as Money)}`;
  };
  return (
    <Section title={`${first.label} × ${second.label}`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-sm">
          <thead className="text-left text-xs text-texto-tenue">
            <tr>
              <th className="px-2 py-1.5 font-semibold" />
              <th className="px-2 py-1.5 text-right font-semibold">{first.label}</th>
              <th className="px-2 py-1.5 text-right font-semibold">{second.label}</th>
              <th className="px-2 py-1.5 text-right font-semibold">Diferença</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {(
              [
                ['Peças', sumParts(a), sumParts(b)],
                ['Mão de obra', a.laborTotal, b.laborTotal],
                ['Total geral', a.grossTotal, b.grossTotal],
                ['Líquido geral', a.netTotal, b.netTotal],
              ] as const
            ).map(([label, x, y]) => (
              <tr key={label} className="border-t border-borda-suave">
                <th className="px-2 py-1 text-left font-normal">{label}</th>
                <td className="px-2 py-1 text-right whitespace-nowrap">{format(x)}</td>
                <td className="px-2 py-1 text-right whitespace-nowrap">{format(y)}</td>
                <td className="px-2 py-1 text-right whitespace-nowrap">{delta(x, y)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {diff.length === 0 ? (
        <Wait>As linhas são as mesmas nos dois orçamentos.</Wait>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="text-left text-xs text-texto-tenue">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Linha</th>
                <th className="px-2 py-1.5 font-semibold">O que mudou</th>
                <th className="px-2 py-1.5 text-right font-semibold">{first.label}</th>
                <th className="px-2 py-1.5 text-right font-semibold">{second.label}</th>
                <th className="px-2 py-1.5 text-right font-semibold">Diferença</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {diff.map((d) => {
                const item = d.second ?? d.first!;
                return (
                  <tr key={d.key} className="border-t border-borda-suave">
                    <td className="px-2 py-1">
                      {item.description}
                      {item.partCode && (
                        <span className="ml-1 text-xs text-texto-suave">
                          {item.partCode}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {d.changes.map((c) => CHANGE_LABELS[c]).join(', ')}
                      {item.coverage === 'sem_amparo' && ' · sem amparo, fora do total'}
                    </td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      {d.first ? format(quoteItemTotal(d.first, first.rates)) : '—'}
                    </td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      {d.second ? format(quoteItemTotal(d.second, second.rates)) : '—'}
                    </td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      {d.delta === 0
                        ? 'igual'
                        : `${d.delta > 0 ? '+' : '−'} ${format(Math.abs(d.delta) as Money)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

const sumParts = (t: ReturnType<typeof repairQuoteTotals>) =>
  (t.partsByWorkshop + t.partsByAssociation) as Money;

/* ------------------------------------------------------------------------- *
 * Aprovação Supervisor — os dois, lado a lado
 * ------------------------------------------------------------------------- */

function QuoteReview({ data }: { data: RepairQuotesData }) {
  const cilia = serverDomain(data.quotes.cilia);
  const own = serverDomain(data.quotes.proprio);
  const participation = participationOf(data);
  if (cilia.items.length === 0 && own.items.length === 0) {
    return (
      <Section title="Orçamento item a item">
        <Wait>
          A Orçamentação não montou orçamento item a item. Vale o que a Regulagem digitou.
        </Wait>
      </Section>
    );
  }
  const only = own.items.length > 0 ? own : cilia;
  const onlyName =
    own.items.length > 0
      ? REPAIR_QUOTE_SOURCE_NAMES.proprio
      : REPAIR_QUOTE_SOURCE_NAMES.cilia;
  return (
    <Section
      title="Orçamento item a item"
      aside={
        data.approved
          ? `aprovado: ${REPAIR_QUOTE_SOURCE_NAMES[data.approved]}`
          : 'escolha em "Orçamento aprovado"'
      }
    >
      {cilia.items.length > 0 && own.items.length > 0 ? (
        <Comparison
          first={{ label: REPAIR_QUOTE_SOURCE_LABELS.cilia, ...cilia }}
          second={{ label: REPAIR_QUOTE_SOURCE_LABELS.proprio, ...own }}
          participation={participation}
        />
      ) : (
        <>
          <p className="text-sm text-texto-suave">
            Só o {onlyName} foi montado: não há com o que comparar.
          </p>
          <Ledger
            totals={repairQuoteTotals(only.items, only.rates, participation)}
            fipe={fromColumn(data.fipeValue)}
          />
        </>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------------- *
 * Compra de Peças — o que a associação compra
 * ------------------------------------------------------------------------- */

function AssociationParts({ data }: { data: RepairQuotesData }) {
  if (!data.approved) {
    return (
      <Section title="Peças pela associação">
        <Wait>
          A Aprovação Supervisor não escolheu um orçamento item a item: as peças da
          associação não têm de onde vir.
        </Wait>
      </Section>
    );
  }
  const quote = serverDomain(data.quotes[data.approved]);
  const parts = quote.items.filter(
    (i) =>
      i.kind === 'peca' && i.supplier === 'associacao' && i.coverage !== 'sem_amparo',
  );
  const total = parts.reduce<Money>((acc, i) => (acc + quoteItemNet(i)) as Money, ZERO);
  return (
    <Section
      title="Peças pela associação"
      aside={`do ${REPAIR_QUOTE_SOURCE_NAMES[data.approved]}, aprovado`}
    >
      {parts.length === 0 ? (
        <Wait>O orçamento aprovado não tem peça fornecida pela associação.</Wait>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-sm">
            <thead className="text-left text-xs text-texto-tenue">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Peça</th>
                <th className="px-2 py-1.5 font-semibold">Código</th>
                <th className="px-2 py-1.5 font-semibold">Tipo</th>
                <th className="px-2 py-1.5 text-right font-semibold">Qtd.</th>
                <th className="px-2 py-1.5 text-right font-semibold">Valor</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {parts.map((p, n) => (
                <tr key={n} className="border-t border-borda-suave">
                  <td className="px-2 py-1">{p.description}</td>
                  <td className="px-2 py-1">{p.partCode ?? '—'}</td>
                  <td className="px-2 py-1">
                    {p.partOrigin ? REPAIR_PART_ORIGIN_LABELS[p.partOrigin] : '—'}
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {p.quantity.toLocaleString('pt-BR')}
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {format(quoteItemNet(p))}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-texto font-semibold">
                <td className="px-2 py-1" colSpan={4}>
                  Total
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap">
                  {format(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
